import { Dayjs } from "dayjs";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import "dayjs/locale/th";
import DateRangePicker from "../common/DateRangePicker";
import { QrTrendGroupBy } from "../../types/qr-analytics.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
// Borrowed from รายงานการโอน rather than copied: this screen was asked to carry that
// report's filter row, and a copy is two rows that agree today and drift apart at the first
// change to either. `FilterField` owns the label-beside-control geometry and the row
// spacing; `useBranchRouteOptions` owns the rule that a สายส่ง list belongs to one branch.
import FilterField, {
  FILTER_ROW_BOTTOM_SPACING,
  FLOATING_HELPER_TEXT_SX,
} from "../SaleDepositReport/FilterField";
import {
  UNFILTERED_OPTION,
  useBranchRouteOptions,
} from "../SaleDepositReport/useBranchRouteOptions";

dayjs.locale("th");

interface QrDashboardFilterProps {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  onChangeDateRange: (from: Dayjs, to: Dayjs) => void;
  branchCode: string;
  onChangeBranch: (branchCode: string) => void;
  routeCode: string;
  onChangeRoute: (routeCode: string) => void;
  groupBy: QrTrendGroupBy;
  onChangeGroupBy: (groupBy: QrTrendGroupBy) => void;
}

const GROUP_BY_OPTIONS: { label: string; value: QrTrendGroupBy }[] = [
  { label: "รายวัน", value: "day" },
  { label: "รายเดือน", value: "month" },
];

/**
 * The dashboard's filter row — วันที่, สาขา, สายส่ง, ช่วงกราฟ.
 *
 * Every filter applies as soon as it changes, with no ค้นหา button, which is what รายงานการโอน
 * does and is safe here for the same reason: none of these controls emits per keystroke. The
 * range picker fires once when a range is confirmed, and the other three are selects.
 *
 * The สาขา picker is offered only to the cross-branch cohort. That is a courtesy, not the
 * guard — the server resolves tenancy from the token and ignores a branch anyone else names,
 * so a hand-edited URL gains nothing.
 */
export default function QrDashboardFilter({
  dateFrom,
  dateTo,
  onChangeDateRange,
  branchCode,
  onChangeBranch,
  routeCode,
  onChangeRoute,
  groupBy,
  onChangeGroupBy,
}: Readonly<QrDashboardFilterProps>) {
  const { isHeadOfficeAccounting, isMaster, isAdmin } = useUserRole();
  const canSelectBranch = Boolean(
    isHeadOfficeAccounting || isMaster || isAdmin,
  );
  const { branches, routeOptions, isRouteDisabled } = useBranchRouteOptions(
    canSelectBranch,
    branchCode,
  );

  // A route belongs to exactly one branch, so a route left selected after the branch changes
  // would filter the report down to nothing while still naming a branch.
  const handleChangeBranch = (nextBranch: string) => {
    onChangeBranch(nextBranch);
    onChangeRoute(UNFILTERED_OPTION);
  };

  return (
    <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING}>
      <FilterField label="วันที่">
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onChangeDateRange}
        />
      </FilterField>

      {canSelectBranch && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={branchCode}
            onChange={(event) => handleChangeBranch(event.target.value)}
          >
            <MenuItem value={UNFILTERED_OPTION}>ทั้งหมด</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.branch_code}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}

      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          disabled={isRouteDisabled}
          value={routeCode}
          onChange={(event) => onChangeRoute(event.target.value)}
          helperText={isRouteDisabled ? "เลือกสาขาก่อน" : undefined}
          sx={FLOATING_HELPER_TEXT_SX}
        >
          <MenuItem value={UNFILTERED_OPTION}>ทั้งหมด</MenuItem>
          {routeOptions.map((route) => (
            <MenuItem key={route.route_code} value={route.route_code}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>

      <FilterField label="ช่วงกราฟ">
        <TextField
          select
          size="small"
          fullWidth
          value={groupBy}
          onChange={(event) =>
            onChangeGroupBy(event.target.value as QrTrendGroupBy)
          }
        >
          {GROUP_BY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
